import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import creditService from '../../../utils/creditService';
import billingService from '../../../utils/billingService';

const BillingSection = () => {
  const [currentPlan, setCurrentPlan] = useState('free');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [creditStats, setCreditStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingHistoryLoading, setBillingHistoryLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState({
    cardNumber: '**** **** **** 4242',
    expiryDate: '12/25',
    cardType: 'visa'
  });

  // Load credit information and billing history
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setBillingHistoryLoading(true);

        // Load credit stats
        const stats = await creditService.getCreditStats();
        setCreditStats(stats);

        if (stats) {
          setCurrentPlan(stats.plan);
          setBillingCycle(stats.billingCycle);
        }

        // Load billing history
        const billingResult = await billingService.getBillingHistory();
        if (billingResult.success) {
          setBillingHistory(billingResult.data);
        } else {
          console.error('Error loading billing history:', billingResult.error);
        }

        // Load payment method
        const paymentResult = await billingService.getPaymentMethod();
        if (paymentResult.success) {
          setPaymentMethod(paymentResult.data);
        }

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
        setBillingHistoryLoading(false);
      }
    };

    loadData();
  }, []);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: { monthly: 19, yearly: 190 },
      validations: '5,000',
      features: [
        'Basic email validation',
        'CSV export',
        'Email support',
        'API access'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: { monthly: 49, yearly: 490 },
      validations: '25,000',
      features: [
        'Advanced validation',
        'All export formats',
        'Priority support',
        'Advanced API features',
        'Bulk validation',
        'Custom integrations'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: { monthly: 149, yearly: 1490 },
      validations: '100,000',
      features: [
        'Premium validation',
        'White-label options',
        'Dedicated support',
        'Custom rate limits',
        'Advanced analytics',
        'SLA guarantee'
      ]
    }
  ];


  const usageStats = creditStats ? {
    currentPeriod: {
      validations: creditStats.used,
      limit: creditStats.total,
      resetDate: creditStats.expiresAt || 'N/A'
    },
    previousPeriod: {
      validations: creditStats.used,
      limit: creditStats.total
    }
  } : {
    currentPeriod: {
      validations: 0,
      limit: 100,
      resetDate: 'N/A'
    },
    previousPeriod: {
      validations: 0,
      limit: 100
    }
  };

  const handlePlanChange = (planId) => {
    if (window.confirm(`Are you sure you want to change to the ${plans.find(p => p.id === planId)?.name} plan?`)) {
      setCurrentPlan(planId);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      setDownloadingInvoice(invoiceId);
      const result = await billingService.downloadInvoice(invoiceId);
      if (result.success) {
        console.log('Invoice downloaded successfully:', result.filename);
      } else {
        console.error('Error downloading invoice:', result.error);
        alert('Failed to download invoice: ' + result.error);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice: ' + error.message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getCardIcon = (type) => {
    switch (type) {
      case 'visa': return 'CreditCard';
      case 'mastercard': return 'CreditCard';
      case 'amex': return 'CreditCard';
      default: return 'CreditCard';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUsagePercentage = () => {
    return Math.round((usageStats.currentPeriod.validations / usageStats.currentPeriod.limit) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Billing Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Billing & Subscription</h2>
        <p className="text-sm text-text-secondary mt-1">
          Manage your subscription, billing information, and usage
        </p>
      </div>

      {/* Current Usage */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Current Usage</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">
                Email Validations
              </span>
              <span className="text-sm text-text-secondary">
                {usageStats.currentPeriod.validations.toLocaleString()} / {creditService.formatCreditsDisplay(usageStats.currentPeriod.limit)}
              </span>
            </div>
            <div className="w-full bg-border-light rounded-full h-2 mb-2">
              <div
                className="progress-bar"
                style={{ width: `${getUsagePercentage()}%` }}
              />
            </div>
            <p className="text-xs text-text-secondary">
              Resets on {formatDate(usageStats.currentPeriod.resetDate)}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Current Plan</span>
              <span className="text-sm font-medium text-text-primary capitalize">
                {creditStats ? creditService.getPlanDisplayName(creditStats.plan, creditStats.billingCycle, creditStats.isUnlimited) : 'Free'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Billing Cycle</span>
              <span className="text-sm font-medium text-text-primary capitalize">
                {creditStats?.isUnlimited ? 'Unlimited' : billingCycle}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Next Billing</span>
              <span className="text-sm font-medium text-text-primary">
                {creditStats?.isUnlimited ? 'Never' : formatDate(usageStats.currentPeriod.resetDate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Plans - HIDDEN */}
      {/* <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-primary">Subscription Plans</h3>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setBillingCycle('monthly')}
              variant={billingCycle === 'monthly' ? 'primary' : 'outline'}
              size="sm"
              animation="slide"
            >
              Monthly
            </Button>
            <Button
              onClick={() => setBillingCycle('yearly')}
              variant={billingCycle === 'yearly' ? 'primary' : 'outline'}
              size="sm"
              animation="bounce"
            >
              Yearly
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative border rounded-lg p-4 ${
                plan.id === currentPlan 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              } ${plan.popular ? 'ring-2 ring-primary' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-white px-3 py-1 text-xs font-medium rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h4 className="text-lg font-semibold text-text-primary">{plan.name}</h4>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-text-primary">
                    ${plan.price[billingCycle]}
                  </span>
                  <span className="text-text-secondary">
                    /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                <p className="text-sm text-text-secondary mt-1">
                  {plan.validations} validations/month
                </p>
              </div>
              
              <ul className="space-y-2 mb-4">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <Icon name="Check" size={16} className="text-success mr-2" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button
                variant={plan.id === currentPlan ? "outline" : "primary"}
                fullWidth
                onClick={() => handlePlanChange(plan.id)}
                disabled={plan.id === currentPlan}
                animation={plan.popular ? 'glow' : plan.id === currentPlan ? 'none' : 'pulse'}
              >
                {plan.id === currentPlan ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          ))}
        </div>
      </div> */}

      {/* Payment Method - HIDDEN */}
      {/* <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-primary">Payment Method</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPaymentForm(!showPaymentForm)}
            iconName="Edit"
            iconPosition="left"
          >
            Update
          </Button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center justify-center w-12 h-8 bg-gray-100 rounded">
            <Icon name={getCardIcon(paymentMethod.cardType)} size={20} className="text-text-secondary" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">
              {paymentMethod.cardNumber}
            </p>
            <p className="text-xs text-text-secondary">
              Expires {paymentMethod.expiryDate}
            </p>
          </div>
        </div>
        
        {showPaymentForm && (
          <div className="mt-4 pt-4 border-t border-border-light">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Card Number
                </label>
                <Input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Expiry Date
                </label>
                <Input
                  type="text"
                  placeholder="MM/YY"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  CVV
                </label>
                <Input
                  type="text"
                  placeholder="123"
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <Button variant="primary" size="sm">
                Update Payment Method
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowPaymentForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div> */}

      {/* Billing History */}
      <div className="card p-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">Billing History</h3>
        </div>

        {billingHistoryLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-text-secondary">Loading billing history...</span>
          </div>
        ) : billingHistory.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Receipt" size={48} className="text-text-secondary mx-auto mb-4" />
            <h4 className="text-lg font-medium text-text-primary mb-2">No billing history</h4>
            <p className="text-text-secondary">You haven't made any payments yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-light">
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-primary">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-primary">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-primary">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-primary">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-text-primary">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((item) => (
                  <tr key={item.id} className="border-b border-border-light hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-text-primary">
                      {formatDate(item.date)}
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary">
                      {item.description}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-text-primary">
                      {item.currency === 'INR' ? 'Rs.' : '$'}{item.amount.toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.status === 'paid' || item.status === 'completed'
                          ? 'bg-success/10 text-success'
                          : 'bg-warning/10 text-warning'
                        }`}>
                        {item.status === 'paid' || item.status === 'completed' ? 'Paid' :
                          item.status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadInvoice(item.invoice)}
                        iconName={downloadingInvoice === item.invoice ? "Loader" : "Download"}
                        iconSize={14}
                        disabled={downloadingInvoice === item.invoice}
                        animation="zoom"
                        className={downloadingInvoice === item.invoice ? "animate-spin" : ""}
                      >
                        {downloadingInvoice === item.invoice ? 'Generating...' : item.invoice}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSection;